import {
  useCallback, useEffect, useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Batch } from 'src/features/stock-movements/StockMovementsTypes'
import {
  selectBarcodeAttempted, selectBarcodeData, selectEntryDataIsComplete, selectReceivementUnitChange,
} from 'src/features/tax-coupon/TaxCouponSelectors'
import { actions } from 'src/features/tax-coupon/TaxCouponState'
import { useLabelPrinting } from 'src/hooks/useLabelPrinting'
import { batchMapperCamelCase } from 'src/utils/mappers/batchMapper'
import { useStore } from 'src/utils/store'
import * as S from './TaxCouponItemForm.styles'
import { ItemData } from '../../../../TaxCouponTypes'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import TaxCouponItemFormWeight from '../TaxCouponItemFormWeight/TaxCouponItemFormWeight'
import TaxCouponItemFormUnit from '../TaxCouponItemFormUnit/TaxCouponItemFormUnit'
import { formatNumberBR } from 'src/utils'
import ModalValidationBadge from '../ModalValidationBadge/ModalValidationBadge'

dayjs.extend(utc)

const barcodeNotFound = 'NAO CADASTRADO'

export default ({ setEnabledButtonFinish }) => {
  const [showModal, setShowModal] = useState(false)
  const dispatch = useDispatch()
  const store = useStore()
  const state = store.getState()
  const { idReceivement, dock, idMeasure } = state.taxCoupon
  const { idModel, description } = useSelector(selectBarcodeData)
  const entryDataIsComplete = useSelector(selectEntryDataIsComplete)
  const receivementUnitChange = useSelector(selectReceivementUnitChange)
  const attempted = useSelector(selectBarcodeAttempted)

  const {
    getValues, setValue, handleSubmit, reset, setFocus, resetField, control, register, formState: { errors },
  } = useForm<ItemData>()

  const { showModalLabelPrinting, labelPrintingComponent } = useLabelPrinting({
    onFinish: () => {
      reset()
      setValue('unitCost', '')
      setTimeout(() => setFocus('barcode'), 400)
    },
  })

  const options = [
    {
      id: 2,
      description: 'UN',
    },
    {
      id: 3,
      description: 'UN - Fracionado',
    },
    {
      id: 4,
      description: 'KG',
    },
    {
      id: 1,
      description: 'KG - Fracionado',
    },
  ]

  const enterKeyCode = 13
  const tabKeyCode = 9

  const handleOnKeyDown = (event) => {
    if (!([enterKeyCode, tabKeyCode].includes(event.keyCode) && !event.shiftKey)) return

    event.preventDefault()
    const barcode = getValues('barcode')
    dispatch(actions.scanBarcode({ barcode, idReceivement }))
    if (!barcode || barcode === '') {
      setFocus('barcode')
    }
  }

  const resetForm = useCallback(() => {
    resetField('boxQuantity')
    resetField('description')
    resetField('barcode')
    resetField('quantity')
    resetField('single')
    setValue('unitCost', '')
    setValue('quantity', '')
    resetField('expirationDate')
    setFocus('barcode')
    resetField('idMeasure')
    dispatch(actions.setBarcodeData({ idModelo: 0, description: '' }))
  }, [resetField, setFocus, setValue, dispatch])

  const setSelectedMeasure = useCallback((value: number) => {
    dispatch(actions.updateIdMeasureUnit({ key: 'idMeasure', value }))
  }, [dispatch])

  const onSubmit = () => {
    if (!idReceivement) return
    setSelectedMeasure(idMeasure)
    const values = getValues()

    values.unitCost = values.unitCost?.slice(3).replace(/[^0-9]/g, '')
    values.quantity = values.quantity?.replace(/[^0-9]/g, '')

    const onSuccess = (batch: Batch, operatorName: string) => {
      dispatch(actions.addItem({
        ...values, idModel, batchNumber: batch.batchNumber, idBatch: batch.idLote,
      }))
      showModalLabelPrinting([batchMapperCamelCase(batch)], operatorName)
    }

    dispatch(actions.createTaxCouponBatch({
      ...values,
      idModel,
      idReceivement,
      dock: Number.parseInt(dock, 10),
      onSuccess,
    }))
    resetForm()
    setEnabledButtonFinish(false)
  }

  useEffect(() => {
    if (attempted && !idModel) {
      setValue('barcode', '')
    }
  }, [attempted, idModel, setValue])

  useEffect(() => {
    if (!idModel || !description) return
    setValue('description', description)
    if (description === barcodeNotFound) {
      resetForm()
      return
    }
    setValue('idMeasure', idMeasure)

    if (idMeasure === 2 || idMeasure === 3) {
      setTimeout(() => setFocus('boxQuantity'), 400)
    }
  }, [description, idModel, setFocus, setValue, resetField, resetForm, idMeasure])

  const currencyFormatter = (formatted_value: any) => {
    if (!Number(formatted_value)) return 'R$ 0,00'
    const br = { style: 'currency', currency: 'BRL' }
    return new Intl.NumberFormat('pt-BR', br).format(formatted_value / 100)
  }

  const formatWeight = (formatted_value: any) => {
    if (!Number(formatted_value)) return formatNumberBR((0).toFixed(3))
    const returnValue = formatNumberBR(Number(formatted_value) / 1000, 3)
    return returnValue
  }

  const unitChange = () => {
    const barcode = getValues('barcode')
    if (barcode) {
      return (
        <S.CellTolltip title='Clique para habilitar' arrow>
          <S.StyledEditIcon
            onClick={() => {
              setShowModal(true)
            }}
            data-testid='receivement-unit-change'
          />
        </S.CellTolltip>
      )
    }
    return <></>
  }

  return (
    <S.ItemForm onSubmit={handleSubmit(onSubmit)}>
      <S.ItemFormContainer>
        <S.Input
          label='barcode'
          name='barcode'
          id='barcode'
          disabled={!entryDataIsComplete}
          onKeyDown={handleOnKeyDown}
          inputProps={{ ...register('barcode', { required: 'Campo obrigatório' }), 'data-testid': 'barcode-test' }}
          error={!!errors.barcode}
          helperText={errors.barcode?.message}
          autoComplete='off'
        />

        <S.Input
          label='taxCoupon.productDescription'
          name='description'
          id='description'
          inputProps={{ ...register('description'), 'data-testid': 'description-test' }}
          InputLabelProps={{ shrink: true }}
          error={!!errors.description}
          helperText={errors.description?.message}
          disabled
        />

        <S.ContainerSelect>
          <S.Select
            autoComplete='off'
            name='idMeasure'
            id='idMeasure'
            inputProps={{ ...register('idMeasure', { required: 'Campo obrigatório' }), 'data-testid': 'measureUnit-test' }}
            label='Unidade de Medida'
            value={idMeasure || 2}
            onChange={(event) => setSelectedMeasure(event.target.value as number)}
            error={!!errors.idMeasure}
            helperText={errors.idMeasure?.message}
            variant='outlined'
            options={options}
            disabled={!entryDataIsComplete || !receivementUnitChange || !description}
            type='text'
            fullWidth
          />
          {unitChange()}
        </S.ContainerSelect>
      </S.ItemFormContainer>
      <ModalValidationBadge showModal={showModal} setShowModal={setShowModal}/>

      {idMeasure === 4 || idMeasure === 1
        ? (
          <TaxCouponItemFormWeight
            entryDataIsComplete={entryDataIsComplete}
            control={control}
            register={register}
            errors={errors}
            currencyFormatter={currencyFormatter}
            formatWeight={formatWeight}
          />
        )
        : (
          <TaxCouponItemFormUnit
            entryDataIsComplete={entryDataIsComplete}
            control={control}
            register={register}
            errors={errors}
            currencyFormatter={currencyFormatter}
          />
        )}

      {labelPrintingComponent}

    </S.ItemForm>
  )
}
